import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntrySchedule } from 'src/components/Schedule/scheduleModel';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  timesheetApprove,
  timesheetBreakCreate,
  timesheetBreakDelete,
  timesheetBreakUpdate,
  timesheetCalendar,
  timesheetEntryCreate,
  timesheetEntryDelete,
  timesheetEntryUpdate,
  timesheetGet,
  timesheetHolidayCreate,
  timesheetHolidayDelete,
  timesheetHolidays,
  timesheetHolidaysLoadVn,
  timesheetKioskConfirm,
  timesheetKioskPunch,
  timesheetKioskRoster,
  timesheetList,
  timesheetMeBreakEnd,
  timesheetMeBreakStart,
  timesheetMeClockIn,
  timesheetMeClockOut,
  timesheetMeConfirm,
  timesheetMeCurrent,
  timesheetMeNote,
  timesheetMeRange,
  timesheetPay,
  timesheetPinClear,
  timesheetPinSet,
  timesheetReopen,
} from 'src/services/apis/tenantRequests';
import { TimesheetCalendarDto } from 'src/services/apis/types';
import { QUERIES, unwrapped } from 'src/utils/react-query';

/**
 * Client copies of the server shapes in apps/server/src/timesheets/timesheetPay.ts and
 * timesheetReview.service.ts. Those controllers return assembled objects with no @ApiResponse,
 * so codegen types them `any` — these declarations are the one place the shapes are written
 * down on this side. Keep them in step with the server files named above.
 */
export type PunchState = 'OUT' | 'IN' | 'BREAK';
export type TimesheetStatus = 'OPEN' | 'APPROVED' | 'PAID';
export type TimesheetWarning = 'OPEN_ENTRY' | 'OPEN_BREAK' | 'GEO_FLAGGED' | 'OVERLAP' | 'LONG_ENTRY';

export type TimesheetDayEntry = {
  id: string;
  shiftName: string | null;
  note: string | null;
  employeeNote: string | null;
  clockInAt: string;
  clockOutAt: string | null;
  edited: boolean;
  geoFlagged: boolean;
  clockInDistanceMeters: number | null;
  open: boolean;
  payableMin: number;
  breaks: { id: string; startAt: string; endAt: string | null; paid: boolean }[];
};

export type TimesheetDay = {
  date: string;
  isoWeekday: number;
  entries: TimesheetDayEntry[];
  workedMin: number;
  paidBreakMin: number;
  unpaidBreakMin: number;
  payableMin: number;
  pay: number;
};

export type TimesheetComputation = {
  days: TimesheetDay[];
  totals: {
    workedMin: number;
    paidBreakMin: number;
    unpaidBreakMin: number;
    payableMin: number;
    pay: number;
    byMultiplier: Record<string, number>;
  };
  warnings: TimesheetWarning[];
};

export type TimesheetRules = {
  timezone: string;
  weekendMultiplier: number;
  holidayMultiplier: number;
  nightPremium: number;
  nightStart: string;
  nightEnd: string;
  holidays: string[];
};

export type TimesheetRecord = {
  id: string;
  restaurantBranchId: string;
  employeeId: string;
  periodStart: string;
  periodEnd: string;
  status: TimesheetStatus;
  employeeConfirmedAt: string | null;
  employeeConfirmedSource: 'SELF' | 'KIOSK' | null;
  approvedAt: string | null;
  paidAt: string | null;
  paymentMethod: 'CASH' | 'TRANSFER' | 'OTHER' | null;
  transactionId: string | null;
  reopenedAt: string | null;
  reopenReason: string | null;
  grossPay: number | null;
};

export type TimesheetHeader = {
  employeeId: string;
  employeeName: string;
  role: string;
  partTime: boolean;
  branchId: string;
  managerName: string | null;
  periodStart: string;
  periodEnd: string;
};

export type TimesheetView = {
  timesheet: TimesheetRecord;
  header: TimesheetHeader;
  hourlyRate: number;
  rules: TimesheetRules;
  computation: TimesheetComputation;
  live: boolean;
  approvedByName: string | null;
  paidByName: string | null;
  /** Detail view only: each entry's planned shift or "unscheduled" label, keyed by entry id. */
  schedule?: Record<string, EntrySchedule>;
};

export type TimesheetListRow = {
  employeeId: string;
  employeeName: string;
  branchId: string;
  timesheet: Pick<TimesheetRecord, 'id' | 'status' | 'periodStart' | 'periodEnd' | 'employeeConfirmedAt'> | null;
  payableMin: number;
  pay: number;
  warnings: TimesheetWarning[];
};

export type KioskRosterRow = {
  employeeId: string;
  displayName: string;
  state: PunchState;
  pinSet: boolean;
  pendingConfirmationId: string | null;
};

export type OpenEntry = {
  id: string;
  clockInAt: string;
  shiftName: string | null;
  breaks: { id: string; startAt: string; endAt: string | null; paid: boolean }[];
};

export type CurrentShift = { state: PunchState; entry: OpenEntry | null; shiftNames: string[]; timezone: string };

export type Holiday = { id: string; date: string; name: string };

// ---- Staff (/me) ----

export const useMyCurrentShift = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  // Polled like the other live screens: a kiosk punch must show up on the phone.
  return useQuery(
    [QUERIES.TIMESHEET_ME_CURRENT, orgSlug, restaurantId],
    () => unwrapped<CurrentShift>(timesheetMeCurrent({ orgSlug, restaurantId })),
    {
      cacheTime: 0,
      refetchInterval: 30_000,
    }
  );
};

export const useMyTimesheets = (from: string, to: string, enabled = true) => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery(
    [QUERIES.TIMESHEET_ME_RANGE, orgSlug, restaurantId, from, to],
    () =>
      unwrapped<{ timesheets: TimesheetView[]; days: TimesheetDay[] }>(
        timesheetMeRange({ orgSlug, restaurantId }, from, to)
      ),
    { enabled }
  );
};

type MyPunch =
  | { action: 'IN'; shiftName?: string; note?: string; lat?: number; lng?: number }
  | { action: 'OUT' }
  | { action: 'BREAK_START'; paid: boolean }
  | { action: 'BREAK_END' };

export const useMyPunch = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  const scope = { orgSlug, restaurantId };
  return useMutation(
    (punch: MyPunch) => {
      switch (punch.action) {
        case 'IN':
          return timesheetMeClockIn(scope, {
            shiftName: punch.shiftName,
            note: punch.note,
            lat: punch.lat,
            lng: punch.lng,
          });
        case 'OUT':
          return timesheetMeClockOut(scope);
        case 'BREAK_START':
          return timesheetMeBreakStart(scope, punch.paid);
        case 'BREAK_END':
          return timesheetMeBreakEnd(scope);
      }
    },
    {
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries([QUERIES.TIMESHEET_ME_CURRENT, orgSlug, restaurantId]),
          queryClient.invalidateQueries([QUERIES.TIMESHEET_ME_RANGE, orgSlug, restaurantId]),
          queryClient.invalidateQueries([QUERIES.SCHEDULE_ME, orgSlug, restaurantId]),
        ]),
    }
  );
};

export const useConfirmMyTimesheet = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation((id: string) => timesheetMeConfirm({ orgSlug, restaurantId }, id), {
    onSuccess: () => queryClient.invalidateQueries([QUERIES.TIMESHEET_ME_RANGE, orgSlug, restaurantId]),
  });
};

export const useSaveEmployeeNote = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation(
    ({ entryId, note }: { entryId: string; note: string | null }) =>
      timesheetMeNote({ orgSlug, restaurantId }, entryId, note),
    { onSuccess: () => queryClient.invalidateQueries([QUERIES.TIMESHEET_ME_RANGE, orgSlug, restaurantId]) }
  );
};

// ---- Manager ----

export const useTimesheetList = (date: string) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery([QUERIES.TIMESHEET_LIST, orgSlug, restaurantId, branchId, date], () =>
    unwrapped<{ period: { start: string; end: string }; rows: TimesheetListRow[] }>(
      timesheetList({ orgSlug, restaurantId, branchId }, date)
    )
  );
};

export const useTimesheet = (id: string | undefined) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.TIMESHEET_DETAIL, orgSlug, restaurantId, branchId, id],
    // Guarded by `enabled` below; TanStack Query never calls this with id undefined.
    () => unwrapped<TimesheetView>(timesheetGet({ orgSlug, restaurantId, branchId }, id as string)),
    { enabled: Boolean(id) }
  );
};

/** Worked shifts by day for the calendar (≤ 42 days per request). The response is typed by codegen. */
export const useTimesheetCalendar = (from: string, to: string, enabled = true) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.TIMESHEET_CALENDAR, orgSlug, restaurantId, branchId, from, to],
    () => timesheetCalendar({ orgSlug, restaurantId, branchId }, from, to) as unknown as Promise<TimesheetCalendarDto>,
    // The app default is refetchOnMount: false; a manager returning from fixing a shift must see it.
    { enabled: enabled && Boolean(from && to), refetchOnMount: true }
  );
};

/** Prefix of every calendar query in the restaurant — any branch, any range. */
const calendarKey = (orgSlug: string, restaurantId: string) => [QUERIES.TIMESHEET_CALENDAR, orgSlug, restaurantId];

/** Every manager write on one timesheet. Each refreshes the detail and the period list. */
export const useTimesheetActions = (id: string) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const queryClient = useQueryClient();
  const scope = { orgSlug, restaurantId, branchId };
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries([QUERIES.TIMESHEET_DETAIL, orgSlug, restaurantId, branchId, id]),
      queryClient.invalidateQueries([QUERIES.TIMESHEET_LIST, orgSlug, restaurantId, branchId]),
      queryClient.invalidateQueries(calendarKey(orgSlug, restaurantId)),
      queryClient.invalidateQueries([QUERIES.SCHEDULES, orgSlug, restaurantId]),
    ]);
  const opts = { onSuccess: refresh };
  return {
    approve: useMutation(() => timesheetApprove(scope, id), opts),
    reopen: useMutation((reason: string) => timesheetReopen(scope, id, reason), opts),
    pay: useMutation((body: Parameters<typeof timesheetPay>[2]) => timesheetPay(scope, id, body), opts),
    createEntry: useMutation(
      (body: Parameters<typeof timesheetEntryCreate>[1]) => timesheetEntryCreate(scope, body),
      opts
    ),
    updateEntry: useMutation(
      ({ entryId, body }: { entryId: string; body: Parameters<typeof timesheetEntryUpdate>[2] }) =>
        timesheetEntryUpdate(scope, entryId, body),
      opts
    ),
    deleteEntry: useMutation((entryId: string) => timesheetEntryDelete(scope, entryId), opts),
    addBreak: useMutation(
      ({ entryId, body }: { entryId: string; body: Parameters<typeof timesheetBreakCreate>[2] }) =>
        timesheetBreakCreate(scope, entryId, body),
      opts
    ),
    updateBreak: useMutation(
      ({ breakId, body }: { breakId: string; body: Parameters<typeof timesheetBreakUpdate>[2] }) =>
        timesheetBreakUpdate(scope, breakId, body),
      opts
    ),
    deleteBreak: useMutation((breakId: string) => timesheetBreakDelete(scope, breakId), opts),
  };
};

/** A manager's first entry for someone with no timesheet in the period yet (no id to key on). */
export const useCreateFirstEntry = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation(
    (body: Parameters<typeof timesheetEntryCreate>[1]) =>
      timesheetEntryCreate({ orgSlug, restaurantId, branchId }, body),
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries([QUERIES.TIMESHEET_LIST, orgSlug, restaurantId, branchId]),
          queryClient.invalidateQueries(calendarKey(orgSlug, restaurantId)),
          queryClient.invalidateQueries([QUERIES.SCHEDULES, orgSlug, restaurantId]),
        ]),
    }
  );
};

export const useSetTimesheetPin = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return {
    set: useMutation(({ employeeId, pin }: { employeeId: string; pin: string }) =>
      timesheetPinSet({ orgSlug, restaurantId }, employeeId, pin)
    ),
    clear: useMutation((employeeId: string) => timesheetPinClear({ orgSlug, restaurantId }, employeeId)),
  };
};

// ---- Kiosk ----

export const useKioskRoster = () => {
  const { orgSlug, restaurantId, branchId, isAggregate } = useTenantContext();
  return useQuery(
    [QUERIES.TIMESHEET_KIOSK_ROSTER, orgSlug, restaurantId, branchId],
    () => unwrapped<KioskRosterRow[]>(timesheetKioskRoster({ orgSlug, restaurantId, branchId })),
    // `branchId=all` has no single roster — the server refuses it, so don't ask every 15 s.
    { cacheTime: 0, refetchInterval: 15_000, enabled: !isAggregate }
  );
};

export const useKioskPunch = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation(
    (body: Parameters<typeof timesheetKioskPunch>[1]) => timesheetKioskPunch({ orgSlug, restaurantId, branchId }, body),
    {
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries([QUERIES.TIMESHEET_KIOSK_ROSTER, orgSlug, restaurantId, branchId]),
          queryClient.invalidateQueries(calendarKey(orgSlug, restaurantId)),
          queryClient.invalidateQueries([QUERIES.SCHEDULES, orgSlug, restaurantId]),
        ]),
    }
  );
};

export const useKioskConfirm = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation(
    (body: Parameters<typeof timesheetKioskConfirm>[1]) =>
      timesheetKioskConfirm({ orgSlug, restaurantId, branchId }, body),
    {
      onSettled: () => queryClient.invalidateQueries([QUERIES.TIMESHEET_KIOSK_ROSTER, orgSlug, restaurantId, branchId]),
    }
  );
};

// ---- Holidays ----

export const useTimesheetHolidays = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery([QUERIES.TIMESHEET_HOLIDAYS, orgSlug, restaurantId], () =>
    unwrapped<Holiday[]>(timesheetHolidays({ orgSlug, restaurantId }))
  );
};

export const useTimesheetHolidayActions = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  const scope = { orgSlug, restaurantId };
  const opts = {
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries([QUERIES.TIMESHEET_HOLIDAYS, orgSlug, restaurantId]),
        queryClient.invalidateQueries(calendarKey(orgSlug, restaurantId)),
      ]),
  };
  return {
    create: useMutation((body: { date: string; name: string }) => timesheetHolidayCreate(scope, body), opts),
    remove: useMutation((id: string) => timesheetHolidayDelete(scope, id), opts),
    loadVn: useMutation((year: number) => timesheetHolidaysLoadVn(scope, year), opts),
  };
};
